import { useCallback } from "react";
import {
  useAdminLoginMutation,
  useResendLoginCodeMutation,
  useVerifyLoginCodeMutation,
} from "@/graphql/generated";
import type { AuthUser } from "@/contexts/AuthContext";
import type { StoredSession } from "@/lib/authStorage";
import {
  getErrorMessage,
  getGraphqlErrorCode,
  getGraphqlErrorMessage,
  getGraphqlErrorNumber,
} from "@/lib/graphqlError";

type SessionPayload = {
  accessToken?: string | null;
  refreshToken?: string | null;
  user?: {
    id: string;
    email: string;
    name?: string | null;
    status: string;
    role?: string | null;
    roles?: string[] | null;
    lastLoginAt?: unknown;
    createdAt?: unknown;
  } | null;
};

export type CodeFailureReason =
  | "WRONG_CODE"
  | "TOO_MANY_TRIES"
  | "TICKET_EXPIRED"
  | "CODE_SEND_FAILED";

export type LoginResult =
  | { ok: true; kind: "session" }
  | { ok: true; kind: "code"; loginTicket: string; maskedEmail: string }
  | {
      ok: false;
      reason:
        | "INVALID_CREDENTIALS"
        | "ACCOUNT_BLOCKED"
        | "ACCOUNT_DISABLED"
        | "NOT_ACTIVATED"
        | "CODE_SEND_FAILED"
        | "UNKNOWN";
      message?: string;
    };

export type VerifyCodeResult =
  | { ok: true }
  | {
      ok: false;
      reason: CodeFailureReason | "UNKNOWN";
      triesLeft?: number;
    };

export type ResendCodeResult =
  | { ok: true; retryAfterSec: number }
  | {
      ok: false;
      reason:
        | "RESEND_TOO_SOON"
        | "TICKET_EXPIRED"
        | "CODE_SEND_FAILED"
        | "UNKNOWN";
      retryAfterSec?: number;
    };

// auth server returns FORBIDDEN for all of these; the message text disambiguates.
const SERVER_LOGIN_MESSAGE = {
  ACCOUNT_DISABLED: "Account is disabled. Contact support.",
  ACCOUNT_NOT_ACTIVE:
    "Account is not active. Please verify your email or contact support.",
} as const;

const CODE_FAILURES: readonly string[] = [
  "WRONG_CODE",
  "TOO_MANY_TRIES",
  "TICKET_EXPIRED",
  "CODE_SEND_FAILED",
];

function toSession(payload?: SessionPayload | null): StoredSession {
  const u = payload?.user;
  if (!payload?.accessToken || !payload.refreshToken || !u)
    throw new Error("Login failed");

  const fromRoles =
    Array.isArray(u.roles) && u.roles.length > 0
      ? String(u.roles[0]).trim()
      : "";
  const fromRole = typeof u.role === "string" ? u.role.trim() : "";
  const role = fromRoles || fromRole;
  if (!role) throw new Error("Login failed: missing user role");

  return {
    token: payload.accessToken,
    refreshToken: payload.refreshToken,
    user: {
      id: u.id,
      email: u.email,
      name: u.name || u.email,
      role: role as AuthUser["role"],
      status: u.status,
      lastLoginAt: u.lastLoginAt ? String(u.lastLoginAt) : undefined,
      createdAt: u.createdAt ? String(u.createdAt) : undefined,
    },
  };
}

function toLoginFailure(e: unknown): Extract<LoginResult, { ok: false }> {
  const code = getGraphqlErrorCode(e);
  const message = getGraphqlErrorMessage(e);

  if (
    code === "UNAUTHORIZED" ||
    code === "INVALID_CREDENTIALS" ||
    code === "UNAUTHENTICATED"
  )
    return { ok: false, reason: "INVALID_CREDENTIALS" };
  if (code === "ACCOUNT_BLOCKED")
    return { ok: false, reason: "ACCOUNT_BLOCKED" };
  if (code === "CODE_SEND_FAILED")
    return { ok: false, reason: "CODE_SEND_FAILED" };

  if (code === "FORBIDDEN") {
    if (message?.startsWith(SERVER_LOGIN_MESSAGE.ACCOUNT_DISABLED))
      return { ok: false, reason: "ACCOUNT_DISABLED" };
    if (message?.startsWith(SERVER_LOGIN_MESSAGE.ACCOUNT_NOT_ACTIVE))
      return { ok: false, reason: "NOT_ACTIVATED" };
    return { ok: false, reason: "UNKNOWN", message };
  }

  return {
    ok: false,
    reason: "UNKNOWN",
    message: getErrorMessage(e, "Login failed"),
  };
}

export function useAdminSession(onSession: (session: StoredSession) => void) {
  const adminLoginMutation = useAdminLoginMutation();
  const verifyMutation = useVerifyLoginCodeMutation();
  const resendMutation = useResendLoginCodeMutation();

  const login = useCallback(
    async (email: string, password: string): Promise<LoginResult> => {
      try {
        const data = await adminLoginMutation.mutateAsync({ email, password });
        const payload = data?.adminLogin;
        if (payload?.loginTicket) {
          return {
            ok: true,
            kind: "code",
            loginTicket: payload.loginTicket,
            maskedEmail: payload.maskedEmail ?? email,
          };
        }
        onSession(toSession(payload));
        return { ok: true, kind: "session" };
      } catch (e) {
        return toLoginFailure(e);
      }
    },
    [adminLoginMutation, onSession],
  );

  const verifyLoginCode = useCallback(
    async (loginTicket: string, code: string): Promise<VerifyCodeResult> => {
      try {
        const data = await verifyMutation.mutateAsync({ loginTicket, code });
        onSession(toSession(data?.verifyLoginCode));
        return { ok: true };
      } catch (e) {
        const errCode = getGraphqlErrorCode(e);
        if (errCode && CODE_FAILURES.includes(errCode))
          return {
            ok: false,
            reason: errCode as CodeFailureReason,
            triesLeft: getGraphqlErrorNumber(e, "triesLeft"),
          };
        return { ok: false, reason: "UNKNOWN" };
      }
    },
    [verifyMutation, onSession],
  );

  const resendLoginCode = useCallback(
    async (loginTicket: string): Promise<ResendCodeResult> => {
      try {
        const data = await resendMutation.mutateAsync({ loginTicket });
        return { ok: true, retryAfterSec: data.resendLoginCode.retryAfterSec };
      } catch (e) {
        const code = getGraphqlErrorCode(e);
        if (
          code === "RESEND_TOO_SOON" ||
          code === "TICKET_EXPIRED" ||
          code === "CODE_SEND_FAILED"
        )
          return {
            ok: false,
            reason: code,
            retryAfterSec: getGraphqlErrorNumber(e, "retryAfterSec"),
          };
        return { ok: false, reason: "UNKNOWN" };
      }
    },
    [resendMutation],
  );

  return { login, verifyLoginCode, resendLoginCode };
}
